import ChatFooter from "@/components/ChatContainer/chatFooter.component";
import MessagesContainer from "@/components/ChatContainer/messagesContainer.component";
import CustomLoader from "@/components/CustomLoader/customLoader.component";
import { getChatHistory, sendNewMessage } from "@/models/chats.model";
import { ChatMessage } from "@/types";
import React, { useLayoutEffect, useRef, useState } from "react";

type ChatContainerProps = {
  type: "singleTaskChat" | "singleTagChat" | "multipleResultsChat";
  chatID: number[];
};

const ChatContainer: React.FC<ChatContainerProps> = ({ chatID, type }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState<ChatMessage | null>(
    null
  );
  const controllerRef = useRef(new AbortController());

  const getMessagesHistory = React.useCallback(async () => {
    controllerRef.current.abort();
    // Create a new AbortController
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    console.log("Getting messages history");
    setMessages([]);
    setLoadingMessages(true);
    setLoadingError(null);
    const response = await getChatHistory({
      type:
        type === "singleTaskChat"
          ? "task"
          : type === "singleTagChat"
            ? "tag"
            : "search",
      chatID: chatID,
      signal: signal,
    });

    if (signal.aborted) {
      return;
    }

    if (response.rStatus === "error") {
      setLoadingError(response.rMessage);
      setLoadingMessages(false);
      return;
    }

    setMessages(response.rData);
    setLoadingMessages(false);
  }, [type, chatID]);

  useLayoutEffect(() => {
    getMessagesHistory();

    // Cleanup function - aborts any ongoing request when dependencies change or component unmounts
    return () => {
      controllerRef.current.abort(
        "Cleanup - ChatContainer dependencies changed or unmounted"
      );
    };
  }, [getMessagesHistory]);

  async function sendMessage(message: string): Promise<boolean> {
    const newMessage: ChatMessage = {
      message: message,
      sender: "user",
      timestamp: Date.now(),
    };
    setSendingMessage(newMessage);

    const response = await sendNewMessage({
      type:
        type === "singleTaskChat"
          ? "task"
          : type === "singleTagChat"
            ? "tag"
            : "search",
      chatID: chatID,
      message: newMessage,
    });

    if (response.rStatus === "error") {
      setSendingMessage(null);
      return false;
    }

    setSendingMessage(null);
    setMessages((prevMessages) => [
      ...prevMessages,
      response.rData.returnMessage,
      response.rData.responseMessage,
    ]);

    return true;
  }

  return loadingMessages ? (
    <CustomLoader />
  ) : loadingError ? (
    <div>{loadingError}</div>
  ) : (
    <div className="flex-grow flex flex-col overflow-hidden">
      <MessagesContainer messages={messages} sendingMessage={sendingMessage} />
      <ChatFooter
        sendMessage={sendMessage}
        isSending={Boolean(sendingMessage)}
        messages={messages}
      />
    </div>
  );
};

export default ChatContainer;
